import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { useCanvasSelection } from "./canvas-selection";
import CanvasIcon from "./CanvasIcon";
import { withRequesterVariables } from "./catalog";
import { useRecommendation } from "./recommendation-guide";
import ScreenFields from "./ScreenFields";
import ScreenShareMenu from "./ScreenShareMenu";
import WorkshopCode from "./WorkshopCode";
import WorkshopEmpty from "./WorkshopEmpty";
import WorkshopNewState from "./WorkshopNewState";
import WorkshopProposal from "./WorkshopProposal";
import WorkshopScreenCard, { CARD_TONES } from "./WorkshopScreenCard";
import WorkshopStates from "./WorkshopStates";
import { useFieldsSlot, useStatesSlot } from "./states-slot";
import WorkshopStateSettings from "./WorkshopStateSettings";
import {
  fillTemplate,
  longCondition,
  WORKSHOP_ICONS,
  WORKSHOP_VIEWS,
} from "./workshop-view";

const VIEW_ICONS = {
  [WORKSHOP_VIEWS.PREVIEW]: WORKSHOP_ICONS.eye,
  [WORKSHOP_VIEWS.CODE]: WORKSHOP_ICONS.code,
};

const SCREEN_VIEWS = [WORKSHOP_VIEWS.PREVIEW, WORKSHOP_VIEWS.CODE];

const WorkshopScreenView = ({
  workshop,
  styleContent,
  variables,
  report,
  fieldsOpen,
  onFieldsOpen,
  share,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const WORKSHOP = t("SCREEN_WORKSHOP", { returnObjects: true });
  const CONDITIONS =
    t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }) || {};
  const stateConditionVariables = withRequesterVariables(variables, CONDITIONS);
  const {
    STATES,
    VIEWS,
    PREVIEW,
    PROPOSAL,
    CODE,
    EMPTY,
    SETTINGS,
    FIELD_LIST,
  } = WORKSHOP;
  const {
    hasScreen,
    locked,
    view,
    states,
    preview,
    code,
    settings,
    proposals,
    issues,
    fields,
  } = workshop;
  const inCode = view.current === WORKSHOP_VIEWS.CODE;
  const targetName = states.nameOf(states.activeKey);
  const newState = proposals.newState;
  const fieldsShown = fieldsOpen && hasScreen && !inCode;
  const waitsForCode = fieldsOpen && hasScreen && inCode && code.edited;
  const { open: switchView } = view;
  const { open: settingsOpen, close: closeSettings } = settings;
  const fieldsSlot = useFieldsSlot();
  const fieldsAside = !isNil(fieldsSlot);
  const settingsShown = settingsOpen && (fieldsAside || !fieldsShown);
  const [focusSearch, setFocusSearch] = useState(false);

  useEffect(() => {
    if (fieldsOpen && hasScreen && inCode && !code.edited)
      switchView(WORKSHOP_VIEWS.PREVIEW);
  }, [fieldsOpen, hasScreen, inCode, code.edited, switchView]);

  const openView = (key) => {
    if (key === WORKSHOP_VIEWS.CODE && fieldsOpen) onFieldsOpen(false);
    view.open(key);
  };

  const toggleFields = () => {
    setFocusSearch(!fieldsOpen);
    if (!fieldsOpen) settings.close();
    onFieldsOpen(!fieldsOpen);
  };

  const toggleSettings = () => {
    if (fieldsAside) {
      settings.toggle();
      return;
    }
    if (fieldsOpen && (!settingsOpen || fieldsShown)) onFieldsOpen(false);
    if (settingsOpen && fieldsShown) return;
    settings.toggle();
  };

  const showFields = (opened) => {
    if (!opened) return;
    setFocusSearch(false);
    if (!fieldsAside) settings.close();
    onFieldsOpen(true);
  };

  const pickSlot = (slotId, additive, pick) => {
    if (additive) {
      if (slotId) preview.cite(slotId);
      return;
    }
    preview.select(slotId, pick);
    showFields(fields.reveal(slotId, pick));
  };

  const { stepId } = useCanvasSelection();
  const statesSlot = useStatesSlot();
  const { focus, clearFocus } = useRecommendation();
  const focusSlotId =
    !isNil(stepId) && get(focus, "stepId") === stepId
      ? get(focus, "slotId") || null
      : null;
  const revealRef = useRef(null);
  revealRef.current = (slotId) => pickSlot(slotId, false);
  useEffect(() => {
    if (isNil(focusSlotId) || !hasScreen) return;
    clearFocus();
    revealRef.current(focusSlotId);
  }, [focusSlotId, hasScreen, clearFocus]);

  const zonesSwitch = (
    <button
      type="button"
      aria-pressed={preview.showZones}
      className="workshop-zones-switch"
      onClick={preview.toggleZones}
    >
      <span className="workshop-zones-switch__track" aria-hidden="true">
        <span className="workshop-zones-switch__knob" />
      </span>
      {PREVIEW.SHOW_ZONES}
    </button>
  );

  const settingsButton = states.active ? (
    <button
      type="button"
      aria-pressed={settingsShown}
      disabled={locked}
      className={classNames("workshop-stage__tool", {
        "workshop-stage__tool--on": settingsShown,
      })}
      onClick={toggleSettings}
    >
      <CanvasIcon path={WORKSHOP_ICONS.sliders} size={16} strokeWidth={1.9} />
      {PREVIEW.SETTINGS}
    </button>
  ) : null;

  const viewSwitch = (
    <div
      role="group"
      aria-label={VIEWS.LABEL}
      className="workshop-stage__views"
    >
      {SCREEN_VIEWS.map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={view.current === key}
          disabled={locked}
          className={classNames("workshop-stage__view", {
            "workshop-stage__view--on": view.current === key,
          })}
          onClick={() => openView(key)}
        >
          <CanvasIcon path={VIEW_ICONS[key]} size={16} strokeWidth={1.9} />
          {VIEWS[key.toUpperCase()]}
        </button>
      ))}
    </div>
  );

  const fieldsButton =
    hasScreen && !fieldsAside ? (
      <button
        type="button"
        aria-pressed={fieldsOpen}
        aria-expanded={fieldsShown}
        className={classNames("workshop-stage__tool", {
          "workshop-stage__tool--on": fieldsOpen,
        })}
        onClick={toggleFields}
      >
        <CanvasIcon path={WORKSHOP_ICONS.list} size={16} strokeWidth={1.9} />
        {VIEWS.FIELDS}
        {issues ? (
          <span className="workshop-stage__view-badge">{issues}</span>
        ) : null}
      </button>
    ) : null;

  const fieldsPanel = fieldsShown ? (
    <ScreenFields
      workshop={workshop}
      variables={variables}
      report={report}
      focusSearch={focusSearch}
      onClose={() => onFieldsOpen(false)}
      embedded={fieldsAside}
    />
  ) : null;

  const settingsPanel = settingsOpen ? (
    <WorkshopStateSettings
      labels={SETTINGS}
      conditionLabels={CONDITIONS}
      stateName={targetName}
      settings={settings.value}
      variables={stateConditionVariables}
      saving={settings.saving}
      disabled={locked}
      onChange={settings.set}
      onSave={settings.save}
      onDelete={settings.remove}
      onClose={closeSettings}
    />
  ) : null;

  const overlayPanel = (fieldsAside ? null : fieldsPanel) || settingsPanel;

  const fieldsAsideContent = waitsForCode ? (
    <p className="workshop-screen-tab__notice" role="status">
      {FIELD_LIST.SAVE_CODE_FIRST}
    </p>
  ) : (
    fieldsPanel
  );

  const statesPicker = (
    <WorkshopStates
      labels={STATES}
      tabs={states.tabs}
      activeKey={states.activeKey}
      disabled={locked}
      onSelect={states.select}
      newState={{
        on: newState.open,
        disabled: locked,
        onToggle: () => newState.setOpen(true),
      }}
    />
  );

  return (
    <div className="workshop-screen-tab">
      {hasScreen && isNil(statesSlot) ? statesPicker : null}
      {hasScreen && !isNil(statesSlot)
        ? createPortal(statesPicker, statesSlot)
        : null}
      {fieldsAside && fieldsSlot.panel && hasScreen
        ? createPortal(fieldsAsideContent, fieldsSlot.panel)
        : null}
      {fieldsAside && fieldsSlot.badge && hasScreen && issues
        ? createPortal(
            <span className="workshop-stage__view-badge">{issues}</span>,
            fieldsSlot.badge,
          )
        : null}
      {hasScreen && newState.open ? (
        <WorkshopNewState
          labels={WORKSHOP.NEW_STATE}
          busy={proposals.generating}
          disabled={locked}
          onPropose={newState.propose}
          onCancel={() => newState.setOpen(false)}
        />
      ) : null}
      {proposals.pending ? (
        <>
          {fieldsPanel && !fieldsAside ? (
            <div className="workshop-screen-tab__stage">
              <div className="workshop-screen-tab__overlay">{fieldsPanel}</div>
            </div>
          ) : null}
          <WorkshopProposal
            key={proposals.batch}
            proposals={proposals.list}
            styleContent={styleContent}
            labels={PROPOSAL}
            captions={PREVIEW}
            defaultName={STATES.DEFAULT}
            applying={proposals.applying || proposals.generating}
            describe={(conditions) =>
              longCondition(conditions, variables, CONDITIONS) || null
            }
            onApply={proposals.apply}
            onCancel={proposals.discard}
          />
        </>
      ) : (
        <>
          {inCode ? (
            <WorkshopCode
              tools={
                <>
                  {viewSwitch}
                  {fieldsButton}
                  {waitsForCode && !fieldsAside ? (
                    <span className="workshop-screen-tab__notice" role="status">
                      {FIELD_LIST.SAVE_CODE_FIRST}
                    </span>
                  ) : null}
                </>
              }
              labels={CODE}
              value={code.value}
              hasScreen={hasScreen}
              isState={Boolean(states.active)}
              targetName={targetName}
              disabled={code.off}
              onChange={code.set}
              onSave={code.save}
            />
          ) : null}
          {!inCode && hasScreen ? (
            <div className="workshop-screen-tab__stage">
              <WorkshopScreenCard
                html={preview.html}
                styleContent={styleContent}
                caption={fillTemplate(PREVIEW.CAPTION, { target: targetName })}
                tone={states.active ? CARD_TONES.STATE : CARD_TONES.DEFAULT}
                onSlotSelect={pickSlot}
                zones={preview.zones}
                sample={workshop.sample.toggle}
                tools={
                  <>
                    {zonesSwitch}
                    {settingsButton}
                    {viewSwitch}
                    {fieldsButton}
                    {share ? <ScreenShareMenu share={share} /> : null}
                  </>
                }
              />
              {overlayPanel ? (
                <div className="workshop-screen-tab__overlay">
                  {overlayPanel}
                </div>
              ) : null}
            </div>
          ) : null}
          {!inCode && !hasScreen ? (
            <WorkshopEmpty
              labels={EMPTY}
              busy={proposals.generating}
              disabled={locked}
              onMockup={proposals.fromMockup}
              onCode={() => openView(WORKSHOP_VIEWS.CODE)}
              share={share}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

WorkshopScreenView.propTypes = {
  workshop: PropTypes.object.isRequired,
  styleContent: PropTypes.string,
  variables: PropTypes.array,
  report: PropTypes.object,
  fieldsOpen: PropTypes.bool,
  onFieldsOpen: PropTypes.func,
  share: PropTypes.object,
};

WorkshopScreenView.defaultProps = {
  styleContent: undefined,
  variables: [],
  report: null,
  fieldsOpen: false,
  onFieldsOpen: () => undefined,
  share: null,
};

export default WorkshopScreenView;
